import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download
} from '@mui/icons-material';
import { superAdminService } from '../../../services/superAdmin/superAdminService';
import { exportToExcel } from '../../../utils/excelExport';
import { COUNTRIES } from '../../../constants/countries';
import TemplateLibraryFormModal from './TemplateLibraryFormModal';

const TemplateLibraryPage = () => {
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [countryFilter, setCountryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data, isLoading } = useQuery({
    queryKey: ['superAdminTemplateLibrary', paginationModel.page, paginationModel.pageSize, countryFilter, search],
    queryFn: async () => {
      const result = await superAdminService.getTemplateLibrary({
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        country: countryFilter || undefined,
        search: search || undefined
      });
      if (!result.success) throw new Error(result.message);
      return result;
    }
  });

  const createMutation = useMutation({
    mutationFn: (data) => superAdminService.createTemplateLibrary(data),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Template added to library.', severity: 'success' });
      setFormOpen(false);
      setEditTemplate(null);
      queryClient.invalidateQueries({ queryKey: ['superAdminTemplateLibrary'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to add template.', severity: 'error' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => superAdminService.updateTemplateLibrary(id, data),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Template updated.', severity: 'success' });
      setFormOpen(false);
      setEditTemplate(null);
      queryClient.invalidateQueries({ queryKey: ['superAdminTemplateLibrary'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update template.', severity: 'error' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => superAdminService.deleteTemplateLibrary(id),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Template deleted.', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['superAdminTemplateLibrary'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete template.', severity: 'error' });
    }
  });

  const handleSave = (payload) => {
    if (editTemplate) {
      updateMutation.mutate({ id: editTemplate.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (template) => {
    setEditTemplate(template);
    setFormOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleExport = useCallback(() => {
    if (!data?.data) return;
    exportToExcel({
      title: 'Template Library',
      fileName: 'EasyCheck_TemplateLibrary.xlsx',
      sheetName: 'Templates',
      columns: [
        { header: 'Country', key: 'country', type: 'text' },
        { header: 'Bank Name', key: 'bankName', type: 'text', width: 25 },
        { header: 'Template Name', key: 'templateName', type: 'text', width: 20 },
        { header: 'Height (cm)', key: 'templateHeight', type: 'number' },
        { header: 'Width (cm)', key: 'templateWidth', type: 'number' },
        { header: 'Created', key: 'createdAt', type: 'date' },
      ],
      data: data.data.map(t => ({
        ...t,
        createdAt: t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-GB') : ''
      })),
    });
  }, [data]);

  const columns = [
    {
      field: 'country',
      headerName: 'Country',
      width: 140,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.75rem', fontWeight: 500 }}
        />
      )
    },
    {
      field: 'bankName',
      headerName: 'Bank Name',
      flex: 1,
      minWidth: 180,
      renderCell: ({ value }) => (
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{value}</Typography>
      )
    },
    {
      field: 'templateName',
      headerName: 'Template Name',
      width: 180,
      renderCell: ({ value }) => (
        <Typography sx={{ fontSize: '0.85rem' }}>{value}</Typography>
      )
    },
    {
      field: 'dimensions',
      headerName: 'Dimensions (cm)',
      width: 140,
      valueGetter: (value, row) => `${row.templateHeight} x ${row.templateWidth}`,
      renderCell: ({ value }) => (
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 500 }}>
          {value}
        </Typography>
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 90,
      renderCell: ({ value }) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          size="small"
          color={value ? 'success' : 'default'}
          sx={{ fontSize: '0.7rem' }}
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 100,
      valueFormatter: (params) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleDateString() : '';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Template Library</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search bank name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 200 }}
            InputProps={{ sx: { borderRadius: 2, backgroundColor: 'white' } }}
          />
          <TextField
            select
            size="small"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            InputProps={{ sx: { borderRadius: 2, backgroundColor: 'white' } }}
          >
            <MenuItem value="">All Countries</MenuItem>
            {COUNTRIES.map(c => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            size="small"
            sx={{ borderColor: '#14b8a6', color: '#14b8a6' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => { setEditTemplate(null); setFormOpen(true); }}
            size="small"
            sx={{ backgroundColor: '#14b8a6', '&:hover': { backgroundColor: '#0d9488' } }}
          >
            Add Template
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100vh - 220px)', backgroundColor: 'white', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc' }
          }}
        />
      </Box>

      <TemplateLibraryFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditTemplate(null); }}
        onSave={handleSave}
        template={editTemplate}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateLibraryPage;
